import axios, { AxiosError } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import END_POINT from '../../config/endpoints.constants';
import Messages from '../../config/messages.constants';
import { useToast } from '../../context/ToastContext';
import useLogout from '../../hooks/useLogout';
import { getErrorMessage, getErrorStatus } from '../../lib/error-handling';

const IGNORE_SESSION_API_LIST = [
    END_POINT.LOGIN,
    END_POINT.CUSTOMER,
    END_POINT.CREATE_CUSTOMER,
    END_POINT.FORGOTPWD,
    END_POINT.NEWPWD,
    END_POINT.LOGIN_V1,
];

const NO_ERROR_TOAST_API_LIST = [END_POINT.CUSTOMER, END_POINT.CUSTOMER_VERIFY];

const AxiosInterceptors: React.FC<React.PropsWithChildren> = ({ children }) => {
    const logout = useLogout();
    const toast = useToast();
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const [loading, setLoading] = useState(true);

    const logoutRef = useRef(logout);
    const toastRef = useRef(toast);
    const timeZoneRef = useRef(timeZone);

    logoutRef.current = logout;
    toastRef.current = toast;
    timeZoneRef.current = timeZone;

    useEffect(() => {
        const requestInterceptorId = axios.interceptors.request.use(async (config) => {
            config.headers['cache-control'] = 'no-cache';
            config.headers['App-Name'] = 'userapp';
            config.headers['App-Source'] = 'userapp';
            config.headers['X-Time-Zone'] = `${timeZoneRef.current}`;
            config.withCredentials = true;

            return config;
        });

        const responseInterceptorId = axios.interceptors.response.use(
            (response) => response,
            (error: AxiosError) => {
                const { config } = error;
                const url = config?.url ?? '';
                const status = getErrorStatus(error);

                if (!IGNORE_SESSION_API_LIST.includes(url) && status === 401) {
                    logoutRef.current(false);
                    toastRef.current.error(Messages.TOKEN_EXPIRED);
                } else if (!NO_ERROR_TOAST_API_LIST.includes(url)) {
                    toastRef.current.error(getErrorMessage(error));
                }

                return Promise.reject(error);
            },
        );

        setLoading(false);

        return () => {
            axios.interceptors.request.eject(requestInterceptorId);
            axios.interceptors.response.eject(responseInterceptorId);
        };
    }, []);

    if (loading) {
        return <div>Loading...</div>;
    }
    return <>{children}</>;
};

export default AxiosInterceptors;
